/**
 * @summary - Component that Provides Asset Type Code Listing Page
 * @author Yuvasri S
 * @since 2024-05-28
 */
import React, { useState, useEffect } from "react";
import { Box, Stack, Typography } from "@mui/material";
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import CustomSelectInput from "../commoncomponents/CustomSelectInput";
import CustomDialog from "../commoncomponents/CustomDialog";
import { useNavigate } from "react-router-dom";
import { DefaultCard } from "../../assets/styled-components/card";
import {
  getAuthToken,
  getFacilityId,
  getcompanyId,
} from "../../utility/tokenUtility";
import axios from "axios";
import CreateNewButton from "../commoncomponents/CreateNewButton";
import RefreshExportButtons from "../commoncomponents/RefreshExportButtons";
import { MasterGraph, FemsGraph, BemsGraph } from "../../utility/apiURLS";
import CloseButton from "../commoncomponents/CloseButton";
import PageTitle from "../commoncomponents/PageTitle";
import SectionTitle from "../commoncomponents/SectionTitle";
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';


const AssetTypeCodeList = () => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);

  const serviceOptions = [
    { label: "FEMS", value: "FEMS", id: 1 },
    { label: "BEMS", value: "BEMS", id: 2 },
  ];
  const [service, setService] = useState(serviceOptions[0]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [deletedRowDetails, setDeletedRowDetails] = useState({});
  const navigate = useNavigate();
  const [gridApi, setGridApi] = useState({});

  const columns = [
    {
      field: "assetclassification",
      headerName: "Asset Classification",
      width: 120,
    },
    { field: "assetcategory", headerName: "Asset Type Code Group", width: 180 },
    {
      field: "assetCategoryDescription",
      headerName: "Asset Type Code Group Description",
      width: 180,
    },
    { field: "typecode", headerName: "Asset Type Code", width: 150 },
    { field: "typeDescription", headerName: "Asset Type Code Description", width: 150 },
    ...(service.value === "BEMS" ? [{ field: "frequency", headerName: "Standard PPMFrequency", width: 150 }] : []),
  ];


  const accessData = useAccess();
  const access = utilities.getScreenPermissions(accessData, "ENGM005");
  const [filterModel, setFilterModel] = useState([]);
  const [sortModel, setSortModel] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportCount, setExportCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false);

  const token = getAuthToken("id_token");
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const companyId = getcompanyId("companyId");
  const [errorMessage, setErrorMessage] = useState("");

  const handleServiceChange = (selectedService) => {
    setService(selectedService);
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([]);
  };

  const fetchAssetTypeCodes = async () => {
    setIsLoading(true);
    setRows([]);
    setFilteredRows([])
    try {

      const response = await axios.get(
        `${FemsGraph}/api/AssetTypeCodeList/GetAssetTypeCodeListASync/${companyId},${service.value}?page=${pageIndex}&size=${pageSize}&filterOptions=${JSON.stringify(
          filterModel
        )}&sortOptions=${JSON.stringify(sortModel)}`, config);
      setRowCount(response.data.totalCount);
      setExportCount(response.data.totalCount);

      const dataWithId = response.data.items.map((item) => ({
        ...item,
        id: item.id || item.typecodeId,
        frequency: `${item.intervalunit} ${item.ppmintervaltypeid}`,
      }));

      setRows(dataWithId);
      setFilteredRows(dataWithId);
    } catch (error) {
      console.error("Error fetching asset categories:", error);
    }
    finally {
      setIsLoading(false);
    }
  };
  const fetchExportdata = async () => {
    setIsLoading(true);
    try {

      const response = await axios.get(
        `${FemsGraph}/api/AssetTypeCodeList/GetCommonExporttypecode/${companyId}/${service.value}?filterOptions=${JSON.stringify(
          filterModel
        )}&sortOptions=${JSON.stringify(sortModel)}`, config);
      setExportData(
        response.data.map((item) => ({
          ...item,
          id: item.id || item.typecodeId,
          frequency: `${item.intervalunit} ${item.ppmintervaltypeid}`,
        }))
      );
    } catch (error) {
      console.error("Error fetching asset categories:", error);
    }
    finally {
      setIsLoading(false);
    }

  };
  useEffect(() => {
    fetchAssetTypeCodes();
  }, [filterModel, sortModel, pageIndex, pageSize]);

  const handleCreateAssetTypeCode = () => {
    navigate("register");
  };

  const handleRefresh = () => {
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([])
  };

  const handleEdit = (rowData) => {
    navigate("edit", {
      state: { ...rowData, readOnlyEdit: true, mode: "edit" },
    });
  };

  const handleView = (rowData) => {
    navigate("view", {
      state: { ...rowData, readOnly: true, mode: "view" },
    });
  };

  const deleteAssetTypeCode = async (id) => {
    try {
      await axios.delete(`${MasterGraph}/api/masassettypecodes/${id}`, config);
      fetchAssetTypeCodes();
      return true;
    } catch (error) {
      console.error("Error deleting asset type code:", error);
      if (error.response && error.response.status === 400) {
        setErrorMessage(
          "Record cannot be deleted as it is referred in another record."
        );
      } else {
        setErrorMessage(
          "An error occurred while deleting the Asset type Code. Please try again."
        );
      }
      return false;
    }
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };
  const confirmDelete = async () => {
    if (rowToDelete) {
      const deleteSuccess = await deleteAssetTypeCode(rowToDelete.typecodeId);

      if (deleteSuccess) {
        setDeletedRowDetails({
          code: rowToDelete.typecode,
          desc: rowToDelete.typeDescription,
        });
        setConfirmationDialogOpen(true);
      }

      setDeleteDialogOpen(false);
      setRowToDelete(null);
    }
  };

  return (
    <>
      <PageTitle title={"Asset Type Code"} />
      <DefaultCard>
        <Box
          mb={1}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <SectionTitle title="Asset Type Code List" />
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <CustomSelectInput
              fullWidth={false}
              labelSize={"14px"}
              sx={{ width: 150 }}
              label={"Service"}
              options={serviceOptions}
              value={service}
              onChange={handleServiceChange}
              placeholder={"Select Service"}
              highlight={true}
            />
            {access?.canCreate && <CreateNewButton onClick={handleCreateAssetTypeCode} />}
            <RefreshExportButtons
              export={rows}
              exportFunction={fetchExportdata}
              exportData={exportData}
              refData={gridApi}
              handleRefresh={handleRefresh}
              title={"Asset Type Code"}
            />
          </Stack>
        </Box>
        <CustomAgGrid
          rows={filteredRows}
          columns={columns}
          handleEdit={access?.canEdit && handleEdit}
          handleDelete={access?.canDelete && handleDelete}
          handleView={access?.canView && handleView}
          refSet={gridApi}
          setGridApi={setGridApi}
          isLoading={isLoading}
          pagination={false}
          setFilterModel={setFilterModel}
          setSortModel={setSortModel}
          totalCount={rowCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
          isDateComparator={true}
        />

      </DefaultCard>
      <CloseButton />
      <CustomDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Confirmation!"
        content="Are you sure you want to delete?"
        cancelBtn="No"
        confirmBtn="Yes"
      />
      <CustomDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        title="Deletion Successful"
        content="Record deleted successfully."
        confirmBtn="OK"
        onConfirm={() => setConfirmationDialogOpen(false)}
      />
      <CustomDialog
        open={!!errorMessage}
        onClose={() => setErrorMessage("")}
        onConfirm={() => setErrorMessage("")}
        content={errorMessage}
        confirmBtn="Ok"
      />
    </>
  );
};

export default AssetTypeCodeList;
